import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '../../lib/supabase/client';
import DashboardLayout from '../components/layout/DashboardLayout';
import { FolderKanban, Plus, Edit, Calendar, MapPin, DollarSign, Trash2 } from 'lucide-react';
import '../components/styles.css';

export default function Projects() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    client_id: '',
    status: 'draft',
    start_date: '',
    end_date: '',
    estimated_cost: '',
    actual_cost: '',
    address: '',
    notes: ''
  });

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const supabase = createClient();
        const { data: { user: currentUser } } = await supabase.auth.getUser();

        if (!currentUser) {
          navigate('/crm/login');
          return;
        }

        setUser(currentUser);
      } catch (error) {
        console.error('Erreur d\'authentification:', error);
        navigate('/crm/login');
      }
    };

    checkAuth();
  }, [navigate]);

  useEffect(() => {
    if (user) {
      fetchProjects();
      fetchClients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filterStatus]);

  const fetchClients = async () => {
    try {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('clients')
        .select('id, last_name, company_name, client_type')
        .eq('status', 'actif')
        .order('last_name', { ascending: true });

      if (error) {
        console.error('Erreur lors de la récupération des clients:', error);
      } else {
        setClients(data || []);
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const supabase = createClient();
      
      // Récupérer les projets
      let query = supabase
        .from('projects')
        .select('*')
        .order('created_at', { ascending: false });

      if (filterStatus !== 'all') {
        query = query.eq('status', filterStatus);
      }

      const { data: projectsData, error: projectsError } = await query;

      if (projectsError) {
        throw projectsError;
      }

      // Récupérer les clients associés
      const clientIds = [...new Set((projectsData || []).map(p => p.client_id).filter(Boolean))];
      let clientsData = {};
      
      if (clientIds.length > 0) {
        const { data: clients, error: clientsError } = await supabase
          .from('clients')
          .select('id, last_name, company_name, client_type')
          .in('id', clientIds);

        if (!clientsError && clients) {
          clients.forEach(client => {
            clientsData[client.id] = client;
          });
        }
      }

      // Joindre les données
      const projectsWithClients = (projectsData || []).map(project => ({
        ...project,
        clients: project.client_id ? clientsData[project.client_id] : null
      }));

      setProjects(projectsWithClients);
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la récupération des chantiers: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const supabase = createClient();

    const projectData = {
      ...formData,
      estimated_cost: formData.estimated_cost ? parseFloat(formData.estimated_cost) : null,
      actual_cost: formData.actual_cost ? parseFloat(formData.actual_cost) : null,
      client_id: formData.client_id || null,
      start_date: formData.start_date || null,
      end_date: formData.end_date || null
    };

    if (editingProject) {
      const { error } = await supabase
        .from('projects')
        .update(projectData)
        .eq('id', editingProject.id);

      if (error) {
        console.error('Erreur lors de la mise à jour:', error);
        alert('Erreur lors de la mise à jour du chantier');
      } else {
        setShowForm(false);
        setEditingProject(null);
        resetForm();
        fetchProjects();
      }
    } else {
      const { error } = await supabase
        .from('projects')
        .insert([projectData]);

      if (error) {
        console.error('Erreur lors de la création:', error);
        alert('Erreur lors de la création du chantier');
      } else {
        setShowForm(false);
        resetForm();
        fetchProjects();
      }
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name || '',
      description: project.description || '',
      client_id: project.client_id || '',
      status: project.status || 'draft',
      start_date: project.start_date || '',
      end_date: project.end_date || '',
      estimated_cost: project.estimated_cost || '',
      actual_cost: project.actual_cost || '',
      address: project.address || '',
      notes: project.notes || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce chantier ? Cette action est irréversible.')) {
      return;
    }

    try {
      const supabase = createClient();
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Erreur lors de la suppression:', error);
        alert('Erreur lors de la suppression du chantier');
      } else {
        fetchProjects();
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression du chantier');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      client_id: '',
      status: 'draft',
      start_date: '',
      end_date: '',
      estimated_cost: '',
      actual_cost: '',
      address: '',
      notes: ''
    });
    setEditingProject(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'draft': return 'Brouillon';
      case 'in_progress': return 'En cours';
      case 'completed': return 'Terminé';
      case 'cancelled': return 'Annulé';
      default: return status;
    }
  };

  const getClientName = (project) => {
    if (project.clients) {
      return project.clients.client_type === 'entreprise' 
        ? project.clients.company_name 
        : project.clients.last_name;
    }
    return 'Non assigné';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="text-center py-8">Chargement des chantiers...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Chantiers</h1>
            <p className="mt-2 text-gray-600">Gérez vos chantiers et projets</p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="h-5 w-5" />
            Ajouter un chantier
          </button>
        </div>

        <div className="flex space-x-4 mb-6">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="p-2 border rounded-md"
          >
            <option value="all">Tous les statuts</option>
            <option value="draft">Brouillon</option>
            <option value="in_progress">En cours</option>
            <option value="completed">Terminé</option>
            <option value="cancelled">Annulé</option>
          </select>
        </div>

        {showForm && (
          <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">
              {editingProject ? 'Modifier le chantier' : 'Nouveau chantier'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom du chantier *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full p-2 border rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                  className="w-full p-2 border rounded-md"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client
                  </label>
                  <select
                    value={formData.client_id}
                    onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="">Sélectionner un client</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.client_type === 'entreprise' ? client.company_name : client.last_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Statut *
                  </label>
                  <select
                    required
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="draft">Brouillon</option>
                    <option value="in_progress">En cours</option>
                    <option value="completed">Terminé</option>
                    <option value="cancelled">Annulé</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date de début
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date de fin
                  </label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Coût estimé (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.estimated_cost}
                    onChange={(e) => setFormData({ ...formData, estimated_cost: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Coût réel (€)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.actual_cost}
                    onChange={(e) => setFormData({ ...formData, actual_cost: e.target.value })}
                    className="w-full p-2 border rounded-md"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full p-2 border rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows="3"
                  className="w-full p-2 border rounded-md"
                />
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  {editingProject ? 'Mettre à jour' : 'Créer'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <ul className="divide-y divide-gray-200">
            {projects.length === 0 ? (
              <li className="p-6 text-center text-gray-500">Aucun chantier trouvé.</li>
            ) : (
              projects.map((project) => (
                <li key={project.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <FolderKanban className="h-5 w-5 text-primary-600" />
                        <span className="text-lg font-semibold text-gray-900">
                          {project.name}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
                          {getStatusLabel(project.status)}
                        </span>
                      </div>
                      <div className="space-y-1 text-sm text-gray-600">
                        <p>Client: <span className="font-medium">{getClientName(project)}</span></p>
                        {project.description && (
                          <p>{project.description}</p>
                        )}
                        {project.address && (
                          <p className="flex items-center gap-2">
                            <MapPin className="h-4 w-4" />
                            {project.address}
                          </p>
                        )}
                        {project.start_date && (
                          <p className="flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            Du {new Date(project.start_date).toLocaleDateString('fr-FR')}
                            {project.end_date && ` au ${new Date(project.end_date).toLocaleDateString('fr-FR')}`}
                          </p>
                        )}
                        {(project.estimated_cost || project.actual_cost) && (
                          <p className="flex items-center gap-2">
                            <DollarSign className="h-4 w-4" />
                            {project.estimated_cost && `Estimé: ${project.estimated_cost}€`}
                            {project.estimated_cost && project.actual_cost && ' • '}
                            {project.actual_cost && `Réel: ${project.actual_cost}€`}
                          </p>
                        )}
                        {project.notes && (
                          <p className="text-gray-700 mt-2">{project.notes}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex flex-col items-end space-y-2 ml-4">
                      <span className="text-xs text-gray-500">
                        {new Date(project.created_at).toLocaleDateString('fr-FR')}
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(project)}
                          className="flex items-center gap-1 px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                        >
                          <Edit className="h-4 w-4" />
                          Modifier
                        </button>
                        <button
                          onClick={() => handleDelete(project.id)}
                          className="flex items-center gap-1 px-3 py-1 text-sm font-medium text-red-700 bg-red-100 rounded-md hover:bg-red-200"
                        >
                          <Trash2 className="h-4 w-4" />
                          Supprimer
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))
            )}
          </ul>
        </div>
      </div>
    </DashboardLayout>
  );
}
